'use client';
import InlineStyle from '@/components/InlineStyle';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from '@/hooks/useTranslation';

const LANGS = [
  { code:'en', flag:'🇬🇧', short:'EN', label:'English'  },
  { code:'es', flag:'🇪🇸', short:'ES', label:'Español'  },
  { code:'ar', flag:'🇦🇪', short:'AR', label:'العربية'  },
  { code:'ja', flag:'🇯🇵', short:'JP', label:'日本語'   },
];

export default function LanguageSwitcher({ variant = 'navbar' }) {
  const { locale, setLocale } = useTranslation();
  const [open, setOpen]     = useState(false);
  const [mounted, setMounted] = useState(false);
  const ref = useRef(null);

  // Mount guard — prevents SSR/client mismatch on locale-dependent rendering
  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  // During SSR / before mount, render a neutral placeholder with no locale-dependent content
  if (!mounted) {
    return (
      <div style={{ width: 64, height: 34, borderRadius: 8, background: '#F0F4FF', border: '1px solid #E8ECF4' }} />
    );
  }

  const current = LANGS.find(l => l.code === locale) || LANGS[0];

  return (
    <div ref={ref} style={{ position:'relative' }}>
      {/* Trigger button — flag + short code + chevron */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display:'flex', alignItems:'center', gap:5,
          padding:'7px 11px', borderRadius:8,
          border:'1px solid #E8ECF4',
          background: open ? '#F0F4FF' : '#fff',
          cursor:'pointer', fontFamily:"'Inter Tight',sans-serif",
          transition:'all 0.15s',
        }}
      >
        <span style={{ fontSize:15, lineHeight:1, display:'block' }}>{current.flag}</span>
        <span style={{ fontSize:12, fontWeight:700, color:'#374151', letterSpacing:'0.03em' }}>{current.short}</span>
        <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="#9CA3AF" strokeWidth="2"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s', flexShrink:0 }}>
          <polyline points="2,4 6,8 10,4"/>
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', right:0,
          background:'#fff', border:'1px solid #E8ECF4',
          borderRadius:12, boxShadow:'0 8px 32px rgba(0,0,0,0.1)',
          overflow:'hidden', zIndex:999, minWidth:150,
          animation:'ls-drop 0.12s ease',
        }}>
          <InlineStyle css={`@keyframes ls-drop{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`} />
          {LANGS.map(lang => (
            <button
              key={lang.code}
              onClick={() => { setLocale(lang.code); setOpen(false); }}
              style={{
                display:'flex', alignItems:'center', gap:10,
                width:'100%', padding:'10px 14px',
                border:'none', borderBottom:'1px solid #F0F4FF',
                background: locale === lang.code ? '#F0F4FF' : 'transparent',
                cursor:'pointer', fontFamily:"'Inter Tight',sans-serif",
                textAlign:'left', transition:'background 0.1s',
              }}
            >
              <span style={{ fontSize:18, lineHeight:1 }}>{lang.flag}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight: locale === lang.code ? 700 : 500, color: locale === lang.code ? '#4F6EF7' : '#374151' }}>
                  {lang.label}
                </div>
                <div style={{ fontSize:10, color:'#9CA3AF', letterSpacing:'0.05em', fontWeight:700 }}>{lang.short}</div>
              </div>
              {locale === lang.code && (
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="#4F6EF7" strokeWidth="2.5">
                  <polyline points="3,8 6,11 13,4"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
